import { dateUtils } from "../utils/DateUtils";
import { AbstractEntityService } from "./AbstractEntityService";

export class AvailabilityAndPricingService extends AbstractEntityService {
    constructor(baseUrl) {
        super();
        this.baseUrl = baseUrl;
    }

    propertyPrefix(propertyId) {
        return `${this.baseUrl}/properties/${propertyId}`;
    }

    /**
     * Returns daily prices for given room id and dates range
     * @param {string} propertyId
     * @param {*} roomId
     * @param {*} from a Date object
     * @param {*} to a Date object
     * @returns
     */
    async getDailyPrices(propertyId, roomId, from, to) {
        const fromAsString = dateUtils.getDateString(from);
        const toAsString = dateUtils.getDateString(to);

        const url = `${this.propertyPrefix(propertyId)}/rooms/${roomId}/dailyPrices?from=${fromAsString}&to=${toAsString}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load daily prices for room ${roomId} - please see server logs for more details`);
        }
    }

    /**
     * Returns daily availability for given room id and dates range
     * @param {string} propertyId
     * @param {*} roomId
     * @param {*} from a Date object
     * @param {*} to a Date object
     * @returns
     */
    async getDailyAvailability(propertyId, roomId, from, to) {
        const fromAsString = dateUtils.getDateString(from);
        const toAsString = dateUtils.getDateString(to);

        const url = `${this.propertyPrefix(propertyId)}/rooms/${roomId}/dailyAvailability?from=${fromAsString}&to=${toAsString}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load daily availability for room ${roomId} - please see server logs for more details`);
        }
    }

    /**
     * Returns daily availability and prices for given room id and dates range
     * @param {string} propertyId
     * @param {*} roomId
     * @param {*} from a Date object
     * @param {*} to a Date object
     * @returns
     */
    async getDailyAvailabilityAndPrices(propertyId, roomId, from, to) {
        try {
            const availability = await this.getDailyAvailability(propertyId, roomId, from, to);
            const prices = await this.getDailyPrices(propertyId, roomId, from, to);

            return { availability, prices, from, to };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Returns latest checkout date for the given checkin date considering availability and max length of stay settings
     * @param {string} propertyId
     * @param {*} roomId
     * @param {*} checkin a Date object
     */
    async getLatestCheckoutDate(propertyId, roomId, checkin, ignoreReservationId) {
        const checkinAsString = dateUtils.getDateString(checkin);

        const url = `${this.propertyPrefix(propertyId)}/rooms/${roomId}/latestCheckout?checkin=${checkinAsString}&ignoreOrderId=${ignoreReservationId}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load latest checkout date for room ${roomId} - please see server logs for more details`);
        }
    }

    /**
     * Per-day check-in availability with reason when unavailable.
     * @param {string} propertyId
     * @param {Date} from
     * @param {Date} to
     * @returns {Promise<Record<string, { available: boolean, reason?: string, minLos?: number, multiplyMinLos?: boolean, allowedCheckinDaysOfWeek?: number[], allowedCheckoutDaysOfWeek?: number[] }>>}
     */
    async getCheckinAvailability(propertyId, from, to) {
        const fromAsString = dateUtils.getDateString(from);
        const toAsString = dateUtils.getDateString(to);

        const url = `${this.propertyPrefix(propertyId)}/checkinAvailability?from=${fromAsString}&to=${toAsString}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load check-in availability for property ${propertyId} - please see server logs for more details`);
        }
    }

    /**
     * Per-day check-out availability with reason when unavailable.
     * @param {string} propertyId
     * @param {Date} checkin
     * @returns {Promise<Record<string, { available: boolean, reason?: string, minLos?: number, multiplyMinLos?: boolean, allowedCheckinDaysOfWeek?: number[], allowedCheckoutDaysOfWeek?: number[] }>>}
     */
    async getCheckoutAvailability(propertyId, checkin) {
        const checkinAsString = dateUtils.getDateString(checkin);

        const url = `${this.propertyPrefix(propertyId)}/checkoutAvailability?checkin=${checkinAsString}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load check-out availability for property ${propertyId} - please see server logs for more details`);
        }
    }

    /**
     * Returns total price given checkin and checkout dates, room, and rate plan
     * @param {string} propertyId
     */
    async getTotalPrice(propertyId, roomId, planId, checkin, checkout, adults) {
        const checkinAsString = dateUtils.getDateString(checkin);
        const checkoutAsString = dateUtils.getDateString(checkout);

        const url = `${this.propertyPrefix(propertyId)}/totalPrice?from=${checkinAsString}&to=${checkoutAsString}&planId=${planId}&roomId=${roomId}&adults=${adults}`;

        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, `Unable to load total price for room ${roomId} and rate plan ${planId} - please see server logs for more details`);
        }
    }
}
